import { z } from "zod";
import { IntakeQuestionTypeSchema } from "./intake-submission-schemas";
import { RequestStatusSchema } from "@src/sudozi-schemas/request-status-schema";

const LevelGroupStatus = z.object({
  id: z.string(),
  label: z.string(),
  level: z.number(),
  isManagerApprover: z.boolean(),
  isRequestor: z.boolean(),
  emailSent: z.string().nullable(),
  blockStatus: z
    .enum([
      "PENDING",
      "IN_PROGRESS",
      "WAITING_ON_VENDOR",
      "WAITING_ON_REQUESTER",
      "WAITING_ON_EXTERNAL_PARTY",
    ])
    .nullish(),
  task: z
    .object({
      id: z.string(),
      description: z.string(),
    })
    .nullable(),
  sendVendorFormTask: z
    .object({
      id: z.string(),
    })
    .nullable(),
  startIroncladWorkflow: z
    .object({
      id: z.string(),
    })
    .nullable(),
  levelGroupStatusNetsuitePurchaseOrder: z
    .object({
      id: z.string(),
    })
    .nullable(),
  createJiraIssueTask: z
    .object({
      id: z.string(),
    })
    .nullable(),
  middeskBusinessVerificationTask: z
    .object({
      id: z.string(),
    })
    .nullable(),
  departments: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .array()
    .nullable(),
  businessUnits: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .array()
    .nullable(),
  groups: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .array()
    .nullable(),
  approvers: z.array(
    z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    }),
  ),
});

export type LevelGroupStatus = z.infer<typeof LevelGroupStatus>;

export const IntakeSubmissionWithCustomColumnsSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  createdAt: z.preprocess(
    (val) => (val instanceof Date ? val.toISOString() : val),
    z.string().datetime(),
  ),
  dueDate: z.preprocess(
    (val) => (val instanceof Date ? val.toISOString() : val),
    z.string().datetime().nullable(),
  ),
  intakeCategoryName: z.string(),
  approvedOrRejectedAt: z.preprocess(
    (val) => (val instanceof Date ? val.toISOString() : val),
    z.string().datetime().nullable(),
  ),
  isDraft: z.boolean(),
  status: RequestStatusSchema,
  archived: z.boolean(),
  createdByUserId: z.string(),
  createdByUserName: z.string(),
  createdByUser: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  }),
  invitedByUser: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable(),
  number: z.number(),
  intakeCategoryId: z.string(),
  assigneeUserId: z.string().nullable(),
  assigneeUserName: z.string().nullable(),
  vendorsForSubmissionJson: z.string(),
  createdByUserDepartments: z.string(),
  requestorId: z.string().nullable(),
  requestorName: z.string(),
  requestor: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  }),
  requestorDepartments: z.string(),
  requestorBusinessUnits: z.string(),
  columnAnswers: z.array(
    z.object({
      ruleId: z.string().nullable(),
      questionId: z.string().nullable(),
      ordinal: z.number(),
      columnName: z.string(),
      type: IntakeQuestionTypeSchema.nullable(),
      answers: z.array(
        z.object({
          id: z.string(),
          value: z.union([z.string(), z.number(), z.boolean()]),
          currency: z.string().nullish(),
        }),
      ),
    }),
  ),
  progress: z.preprocess((val) => Number(val), z.number()),
  awaitingApprovalFromLevelGroupStatuses: z.array(LevelGroupStatus).nullable(),
  lastAuditLogMessage: z.string().nullable(),
  lastAuditLogDate: z.preprocess(
    (val) => (val instanceof Date ? val.toISOString() : val),
    z.string().datetime().nullable(),
  ),
  requestAmount: z.number().nullable(),
  coalescedRequestAmount: z.number(),
  currency: z.string().nullable(),
  currentLevelPendingDuration: z.string().nullable(),
  approvedBy: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .array(),
  rejectedBy: z
    .object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .array(),
});

export type IntakeSubmissionWithCustomColumnsSchema = z.infer<
  typeof IntakeSubmissionWithCustomColumnsSchema
>;
